import { useCallback, useEffect, useState } from "react";

export interface UseRefocusAfterSaveOptions {
  isSaving: boolean;
  /** `data-testid` of the Save button. */
  saveTestId: string;
  /**
   * `data-testid` of a nearby action that stays enabled, for when Save is disabled. A function is
   * resolved when focus moves, so it can read the viewport (never during render).
   */
  fallbackTestId: string | (() => string);
}

/**
 * Save disables itself while it has focus — as the save starts, and again once the form is clean —
 * which drops keyboard focus to the page body (WCAG 2.4.3). Call the returned function after a save
 * this control started; once the save has settled, focus goes back to Save if it is enabled, or to
 * `fallbackTestId` if not. Focus that has moved on (to an invalid field, or a dialog) is left alone.
 */
export const useRefocusAfterSave = ({
  isSaving,
  saveTestId,
  fallbackTestId
}: UseRefocusAfterSaveOptions) => {
  const [isAwaitingRefocus, setIsAwaitingRefocus] = useState(false);

  useEffect(() => {
    if (!isAwaitingRefocus || isSaving) {
      return;
    }

    setIsAwaitingRefocus(false);
    const save = document.querySelector<HTMLButtonElement>(
      `[data-testid="${saveTestId}"]`
    );
    const active = document.activeElement;
    if (active && active !== document.body && active !== save) {
      return;
    }

    const fallback =
      typeof fallbackTestId === "function" ? fallbackTestId() : fallbackTestId;
    const target =
      save && !save.disabled
        ? save
        : document.querySelector<HTMLElement>(`[data-testid="${fallback}"]`);
    target?.focus();
  }, [isAwaitingRefocus, isSaving, saveTestId, fallbackTestId]);

  return useCallback(() => {
    setIsAwaitingRefocus(true);
  }, []);
};
